import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DealsService } from '@app/deals/services/deals.service';
import { CustomerStoreService } from '@app/ngrx-customer/services';
import { selectedCustomer } from '@app/ngrx-customer/store';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '@notifications/services/notifications.service';
import { AuthConnectService, PanAppState } from '@panamax/app-state';
import { HeaderService } from '@shared/components/header/header.service';
import { InviteUserModalComponent } from '@shared/components/invite-user-modal/invite-user-modal.component';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { HeaderLinkLabelEnum } from '@shared/constants/header';
import { SEARCH_PAGES_SEARCH_KEYS } from '@shared/constants/search-pages.enum';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { InviteNewUserService } from '@shared/services/invite-new-user/invite-new-user.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { UpcService } from '@shared/services/upc/upc.service';
import { Customer, Division } from '@usf/customer-types';
import { DeliveryStoreService } from '@usf/ngrx-order';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { UserKinds } from '@usf/user-types/user';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  combineLatest,
} from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PunchoutReturnLinkData } from '../punchout/models/punchout-return-link-data.model';
import { PunchoutService } from '../punchout/services/punchout.service';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import { AppService } from '@shared/services/app.service';
import { UserService } from '@app/user/services';
import { ChefStoreQrCodeComponent } from './components/chef-store-qr-code/chef-store-qr-code.component';
import { MorePageViewModel } from './models/more';
import { MoreService } from './services/more.service';
import { DivisionPhoneNumberService } from '@shared/services/division-phone-number/division-phone-number.service';
import { TopSectionSearchText } from '@search/shared/models/search-page.model';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import {
  FACET_VALUE,
  OUR_EXCLUSIVES_FACET,
  USF_PROGRAMS_FACET,
} from '@app/new-search/constants/new-search-constants';
import { GuestPageService } from '@shared/services/guest-page/guest-page.service';

@Component({
  selector: 'app-more',
  templateUrl: './more.page.html',
  styleUrls: ['./more.page.scss'],
})
export class MorePage implements OnInit {
  private destroy$ = new Subject<void>();

  readonly PATHS = PATHS;
  readonly chefStoreQRFlag = FEATURES.split_global_chef_store_qr_code.name;
  readonly dealsFeatureFlag = FEATURES.split_global_deals.name;
  readonly trendViewFeatureFlag = FEATURES.split_global_alerts_trendview.name;
  readonly chefStoreRoleCheckFlag =
    FEATURES.split_global_chef_store_role_check.name;
  readonly superUserFlag = FEATURES.split_global_alerts_super_user.name;
  readonly supportCenterFlag = FEATURES.split_global_support_center.name;
  readonly manageUsersFlag = FEATURES.split_global_manage_users.name;
  readonly internalBusinessAnalyticsFlag =
    FEATURES.split_dynamic_business_analytics.name;
  readonly globalChefStoreQRCodeFlag =
    FEATURES.split_global_chef_store_qr_code_new.name;

  // OBSERVABLES
  divisionInfo$: Observable<Division>;
  moreVM$: Observable<MorePageViewModel>;
  punchoutReturnLinkData$: Observable<PunchoutReturnLinkData>;
  serviceRequestFeatureFlag$: Observable<Boolean>;
  getInviteNewUserVisibility$: Observable<boolean>;
  divisionPhoneNumber$: Observable<string>;
  supportCenterFlag$: Observable<boolean>;
  manageUsersFlag$: Observable<boolean>;
  internalBusinessAnalyticsFlag$: Observable<boolean>;
  showBusinessAnalytics$: Observable<boolean>;
  isPremierUser$: Observable<boolean>;
  isUpcDeprecated$: Observable<boolean> = this.upcService.isUpcDeprecated();

  // SUBSCRIPTIONS
  importOrderModalIsOpen$ = new BehaviorSubject<boolean>(false);
  checkLinkSub$: Subscription = new Subscription();
  customerSub$: Subscription = new Subscription();
  dealsProductsExistSub$: Subscription = new Subscription();
  featureFlagTrendviewSub$: Subscription = new Subscription();
  paymentsLinkFlag$: Subscription = new Subscription();
  paymentsLinkUser$: Subscription = new Subscription();
  platformSub$: Subscription = new Subscription();
  moreVMSub$: Subscription;
  equipmentSuppliesSub$: Subscription = new Subscription();
  isGuestUserAndSearchBrowseFeatureFlagSub$: Subscription;

  // COMPONENT VARS
  chefStoreQREnabled = false;
  checkLink = environment.checkUrl;
  checkLinkHidden: boolean;
  farmerReport = environment.farmersReportUrl;
  invoicePageTitle: string;
  isDealsAvailable: boolean;
  newsPageLabel: string;
  newsPageUrl: string;
  paymentLinkFeatureFlag: boolean;
  recipes = environment.recipesUrl;
  selectedCustomer: Customer;
  serveGoodLink = environment.serveGoodUrl;
  showNewsPage = false;
  trendviewFeatureFlagEnabled: boolean;
  userSiteRole: string;
  isSuperUser = false;
  showEquipmentSupplies = false;
  isGuestUserAndSearchBrowseFeatureFlag: boolean;
  isGuestUser: boolean;
  isNewSearchEnabled: boolean;
  isCustomerAdmin = false;
  hideImportOptions = false;

  pageTitle = PageTitle.menu;

  constructor(
    protected router: Router,
    protected store: Store,
    protected modalController: ModalController,
    readonly panAppState: PanAppState,
    private appService: AppService,
    private authConnectService: AuthConnectService,
    private customerStoreService: CustomerStoreService,
    private dealsService: DealsService,
    private headerService: HeaderService,
    private modalService: ModalService,
    protected moreService: MoreService,
    protected navigationHelperService: NavigationHelperService,
    private notificationsService: NotificationsService,
    private punchoutService: PunchoutService,
    private upcService: UpcService,
    private userService: UserService,
    private deliveryStoreService: DeliveryStoreService,
    protected analyticsService: ProductAnalyticsService,
    private inviteNewUserService: InviteNewUserService,
    private translateService: TranslateService,
    private divisionPhoneNumberService: DivisionPhoneNumberService,
    private toggleNewSearchService: ToggleNewSearchService,
    private guestPageService: GuestPageService,
  ) {}

  ngOnInit() {
    this.platformSub$ = this.panAppState.platform$
      .pipe(takeUntil(this.destroy$))
      .subscribe(platform => {
        if (platform.platformTypes.includes('desktop')) {
          this.router.navigate([PATHS.HOME]);
        }
      });
    this.getOurExclusivesVisibility();
    this.customerStoreService.loadDivisionPhoneNumbers();
    this.isPremierUser$ = this.userService.userProfiles$().pipe(
      map(userProfiles =>
        userProfiles.some(
          profile =>
            profile.key.toLowerCase() === 'axerossouser' &&
            profile.value.toLowerCase() === 'y',
        ),
      ),
      takeUntil(this.destroy$),
    );
    this.paymentsLinkFlag$ = this.panAppState
      .feature$([FEATURES.split_user_payments_link.name])
      .pipe(takeUntil(this.destroy$))
      .subscribe(featureFlag => {
        if (featureFlag) {
          this.paymentsLinkUser$ = this.panAppState.user$
            .pipe(takeUntil(this.destroy$))
            .subscribe(async params => {
              if (params?.userId) {
                this.paymentLinkFeatureFlag = featureFlag;
              }
            });
        }
      });
    this.serviceRequestFeatureFlag$ = this.panAppState
      .feature$([FEATURES.split_global_service_request.name])
      .pipe(takeUntil(this.destroy$));
    this.featureFlagTrendviewSub$ = this.panAppState
      .feature$([this.trendViewFeatureFlag])
      .pipe(takeUntil(this.destroy$))
      .subscribe(featureFlag => {
        if (featureFlag) {
          this.trendviewFeatureFlagEnabled = featureFlag;
        }
      });
    this.supportCenterFlag$ = this.panAppState.feature$([
      this.supportCenterFlag,
    ]);
    this.manageUsersFlag$ = this.panAppState.feature$([this.manageUsersFlag]);
    this.internalBusinessAnalyticsFlag$ = this.panAppState.feature$([
      this.internalBusinessAnalyticsFlag,
    ]);
    this.showBusinessAnalytics$ = combineLatest(
      [
        this.internalBusinessAnalyticsFlag$,
        this.userService.showBusinessAnalytics$(),
      ],
      (baFlag, showBa) => baFlag && showBa,
    );

    this.toggleNewSearchService.newSearchEnabledObservable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        isNewSearchEnabled => (this.isNewSearchEnabled = isNewSearchEnabled),
      );
  }

  ionViewWillEnter() {
    this.panAppState.setPageTitle(
      this.translateService.instant(this.pageTitle),
    );
    this.moreVM$ = this.moreService.getMoreVM$();

    this.moreVMSub$ = this.moreVM$
      .pipe(takeUntil(this.destroy$))
      .subscribe(vm => {
        this.userSiteRole = vm?.userSiteRole;
        this.hideImportOptions = this.userSiteRole === 'NO_SUBMIT';
        this.isCustomerAdmin = vm?.isCustomerAdmin;
        if (!vm?.hideInvoiceAndPayments) {
          this.invoicePageTitle = vm.canPayInvoice
            ? 'i18n.moxe-links.invoicesAndPayments'
            : 'i18n.moxe-links.invoices';
        }
        if (
          vm?.showNewsLink &&
          vm?.newsPageData?.label?.length &&
          vm?.newsPageData?.url?.length
        ) {
          this.showNewsPage = true;
          this.newsPageLabel = vm.newsPageData.label;
          this.newsPageUrl = vm.newsPageData.url;
        }
      });
    this.customerSub$ = this.panAppState?.customer$
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.divisionInfo$ =
          this.customerStoreService.loadDivisionByDivisionNumber$(
            res?.divisionNumber,
          );
        this.divisionPhoneNumber$ = this.divisionPhoneNumberService
          .getDivisionPhoneNumber$(
            res?.customerType === 'NA',
            res?.divisionNumber,
          )
          ?.pipe(map(phoneNumber => phoneNumber));
      });
    this.punchoutReturnLinkData$ =
      this.punchoutService.punchoutReturnLinkData$();
    this.notificationsService.checkMissedNotification();
    this.getInviteNewUserVisibility$ = this.inviteNewUserService
      .getInviteNewUserVisibility()
      ?.pipe(take(1));
    combineLatest([
      this.panAppState?.feature$([this.superUserFlag]),
      this.userService.isSuperUser$(),
    ])
      .pipe(
        map(([flagEnabled, isSuperUser]) => {
          return {
            flagEnabled,
            isSuperUser,
          };
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(res => {
        if (res?.flagEnabled) {
          this.isSuperUser = res?.isSuperUser;
        }
      });
    this.equipmentSuppliesSub$ = this.panAppState
      .feature$([FEATURES.split_global_equipment_and_supplies.name])
      .pipe(takeUntil(this.destroy$))
      .subscribe(on => {
        if (on) {
          this.showEquipmentSupplies = on;
        }
      });

    this.isGuestUserAndSearchBrowseFeatureFlagSub$ = combineLatest([
      this.panAppState.feature$([FEATURES.split_global_search_browse.name]),
      this.userService.isGuestUser$(),
    ]).subscribe(([featureFlag, isGuestUser]) => {
      this.isGuestUser = isGuestUser;
      this.isGuestUserAndSearchBrowseFeatureFlag = featureFlag || isGuestUser;
    });
  }

  ionViewDidEnter() {
    this.checkChefStoreQREligibility();
    this.calculateIfDealsAreAvailable();
  }

  ionViewWillLeave() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openUPC() {
    this.upcService.openUPC();
  }

  getOurExclusivesVisibility() {
    this.checkLinkSub$ = this.userService
      .showValueAddedServices$()
      ?.pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.checkLinkHidden = !res;
      });
  }

  async logout(): Promise<void> {
    await this.authConnectService.logout();
  }

  routeToWithinMoreTab(path: string) {
    this.router.navigate([path]);
  }

  routeTo(path: string[]) {
    this.router.navigate(['touch', ...path]);
  }

  routeToBrowse() {
    if (this.isGuestUserAndSearchBrowseFeatureFlag) {
      this.router.navigate(['touch', 'search', 'browse']);
    } else {
      this.router.navigate(['touch', PATHS.SEARCH]);
    }
  }

  routeToSuppliesLast() {
    if (this.isNewSearchEnabled) {
      this.navigationHelperService.routeToNewListingPage(
        SEARCH_PAGES_SEARCH_KEYS.whileSuppliesLast,
      );
    } else {
      this.navigationHelperService.routeToSearch({
        searchText: SEARCH_PAGES_SEARCH_KEYS.whileSuppliesLast,
      });
    }
  }

  routeToImportedOrders() {
    this.navigationHelperService.routeTo(PATHS.IMPORTED_ORDERS);
  }

  routeToQuickOrderEntry() {
    this.navigationHelperService.routeTo(PATHS.QUICK_ORDER_ENTRY);
  }

  routeToTrendview360() {
    this.headerService.navigateWithClaimTicket(
      environment.tv360Url,
      HeaderLinkLabelEnum.tv360,
    );
  }

  routeToScoop() {
    this.analyticsService.trackClickScoopBanner();
    if (this.isNewSearchEnabled) {
      this.navigationHelperService.routeToNewSearch({
        facetFilters: `${OUR_EXCLUSIVES_FACET}:${FACET_VALUE.ALL_SCOOP}`,
      });
    } else {
      this.navigationHelperService.routeToSearch({
        searchText: TopSectionSearchText.ALLSCOOP,
      });
    }
  }

  routeToRebates(takeoutOption: string) {
    this.analyticsService.trackClickRebatesLink();
    this.routeTo(['customer-incentives', takeoutOption]);
  }

  routeToEquipmentAndSupplies() {
    this.analyticsService.trackEquipmentAndSupplies({
      buttonName: 'equipment & supplies:header',
      promoId: 'EandSCoreEssentials',
    });
    const queryParams = {
      searchText: 'eandscoreessentials',
    };
    if (this.isNewSearchEnabled) {
      this.navigationHelperService.routeToNewSearch(queryParams);
    } else {
      this.navigationHelperService.routeToSearch(queryParams);
    }
  }

  routeToServeYou() {
    if (this.isNewSearchEnabled) {
      this.navigationHelperService.routeToNewSearch({
        facetFilters: `${USF_PROGRAMS_FACET}:${FACET_VALUE.SERVE_YOU}`,
      });
    } else {
      this.navigationHelperService.routeToSearch({
        searchText: TopSectionSearchText.SERVE_YOU,
      });
    }
  }

  routeToNewsPage() {
    if (this.newsPageUrl?.length) {
      window.open(this.newsPageUrl, '_blank');
    }
  }

  routeToPayments() {
    if (this.paymentLinkFeatureFlag) {
      this.router.navigate(['touch', PATHS.PAYMENTS]);
    } else {
      window.open(environment.myAccountUrl, '_blank');
    }
  }

  routeToExternalWeb(path: string) {
    this.appService.openUrlInBrowser(path);
  }

  routeToImageOrderingEntry() {
    this.navigationHelperService.routeTo(PATHS.IMAGE_ORDERING);
  }

  returnToPartnerSite(partnerSiteLink: string) {
    window.location.replace(partnerSiteLink);
  }

  routeToRecipes() {
    this.headerService.navigateWithClaimTicket(
      environment.recipesUrl,
      HeaderLinkLabelEnum.recipes,
    );
  }

  routeToSellerShowcase() {
    this.navigationHelperService.routeTo(PATHS.SELLER_SHOWCASE_PRODUCTS);
  }

  calculateIfDealsAreAvailable() {
    this.dealsProductsExistSub$ = this.dealsService
      .selectDealsProductsExist$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(dealsProductsExist => {
        this.isDealsAvailable = dealsProductsExist;
      });
  }

  checkChefStoreQREligibility() {
    combineLatest([
      this.panAppState?.feature$([this.chefStoreQRFlag]),
      this.store?.select(selectedCustomer),
      this.panAppState?.user$,
      this.panAppState?.feature$([this.chefStoreRoleCheckFlag]),
      this.deliveryStoreService?.getNextDeliveryInformation(),
    ])
      .pipe(
        map(
          ([
            flagEnabled,
            selectedCustomer,
            user,
            roleCheckFlagEnabled,
            nextDeliveryInfo,
          ]) => {
            return {
              flagEnabled,
              selectedCustomer,
              user,
              roleCheckFlagEnabled,
              nextDeliveryInfo,
            };
          },
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(res => {
        if (
          res?.flagEnabled &&
          new Date(res?.selectedCustomer?.dormantDate) >= new Date() &&
          (res?.user?.userKind === UserKinds.External ||
            res?.user?.userKind === UserKinds.Internal)
        ) {
          if (res?.roleCheckFlagEnabled) {
            if (this.userSiteRole === 'NO_SUBMIT') {
              this.chefStoreQREnabled = false;
              this.selectedCustomer = null;
            } else if (!res?.nextDeliveryInfo?.creditIndicator) {
              this.chefStoreQREnabled = false;
              this.selectedCustomer = null;
            } else {
              this.chefStoreQREnabled = true;
              this.selectedCustomer = res?.selectedCustomer;
            }
          } else if (!res?.nextDeliveryInfo?.creditIndicator) {
            this.chefStoreQREnabled = false;
            this.selectedCustomer = null;
          } else {
            this.chefStoreQREnabled = true;
            this.selectedCustomer = res?.selectedCustomer;
          }
        } else {
          this.chefStoreQREnabled = false;
          this.selectedCustomer = null;
        }
      });
  }

  async openChefStoreQRCodeModal(userId: string, userName: string) {
    this.modalService.setModalOptions(true, ModalWithNavComponent, {
      rootPage: ChefStoreQrCodeComponent,
      rootPageParams: {
        customer: this.selectedCustomer,
        userId: userId,
        userName: userName,
      },
    });
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  async openInviteNewUserModal() {
    this.modalService.setModalOptions(
      true,
      ModalWithNavComponent,
      {
        rootPage: InviteUserModalComponent,
      },
      'invite-new-user-modal',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    return await modal.present();
  }

  onBecomeACustomerClick() {
    this.guestPageService.trackBecomeACustomerButtonClickAndRedirect();
  }
}
